import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import useAxios from 'hooks/useAxios';
import useAuth from 'hooks/useAuth';
import LoaderSpinner from 'utils/LoaderSpinner';
import BreadcrumbWrapper from './BreadcrumbWrapper';
import GridItem from './GridItem';
import { config } from 'config';
import SliderFull from '../home/SliderFull';

const META_PIXEL_ID = '364011971871528';
const TARGET_CATEGORY_ID = 'semanadelbienestarintegral';
const SPECIAL_CATEGORY_ID = 'efp';
const BANNER_CATEGORY_ID = 'd7f027cbf3bb40f39ab1cb723c98b26d';

const CategoryGridItems = ({ category_id, specialRegister }) => {
    const { response, loading } = useAxios({
        method: 'get',
        url: `aos-vod/category/${category_id}?tenant_id=tecmilenio&category_id=${category_id}`
    });

    if (loading || !response) return null;

    return response.category_list
        .filter(video => video.public_see === 1)
        .map(video => (
            <GridItem
                key={video.stream_id}
                {...video}
                specialRegister={specialRegister}
            />
        ));
};

const ensureMetaPixelLoaded = (pixelId) => {
    // Evita reinyectar si ya existe
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') return;

    // Meta Pixel Code
    /* eslint-disable */
    !(function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = (f.fbq = function () {
            n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        });
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    /* eslint-enable */

    window.fbq('init', pixelId);
};

const CategoryPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isLoggedIn } = useAuth();
    const [vodList, setVodList] = useState([]);
    const [breadcrumbId, setBreadcrumbId] = useState([]);
    const [isEmptyVodList, setIsEmptyVod] = useState(true);

    useEffect(() => {
        if (id === SPECIAL_CATEGORY_ID) {
            document.body.classList.add('theme-special-category');
        } else {
            document.body.classList.remove('theme-special-category');
        }
        return () => {
            document.body.classList.remove('theme-special-category');
        };
    }, [id]);

    const { response, loading, error } = useAxios({
        method: 'get',
        url: `aos-vod/category/${id}?category_id=${id}&tenant_id=tecmilenio`
    });

    const { response: childCategoriesResponse, loading: childCategoriesLoading } = useAxios({
        method: 'get',
        url: id === SPECIAL_CATEGORY_ID ? `/aos-category/parent_id/${id}` : null
    });

    const categoriesOrder = (a, b) => {
        if (a.order_by < b.order_by) return -1;
        if (a.order_by > b.order_by) return 1;
        return 0;
    };

    // Cargar Pixel y registrar PageView SOLO cuando el id sea el target
    useEffect(() => {
        if (id !== TARGET_CATEGORY_ID) return;

        ensureMetaPixelLoaded(META_PIXEL_ID);

        if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
            window.fbq('track', 'PageView');
        }

        // Fallback equivalente al <noscript> (útil si el script tarda o está bloqueado)
        // No rompe nada y solo se dispara para la categoría objetivo.
        if (typeof window !== 'undefined') {
            const img = new Image(1, 1);
            img.src = `https://www.facebook.com/tr?id=${encodeURIComponent(
              META_PIXEL_ID
            )}&ev=PageView&noscript=1`;
        }
    }, [id]);

    // Procesar respuesta + track de "categorías" SOLO cuando el id sea el target
    useEffect(() => {
        if (response === null) return;

        let list = [];
        if (response.category_list.length > config.consts.NO_ITEMS) {
            for (const video of response.category_list) {
                if (video.public_see === 1) list.push(video);
            }
            setVodList(list);
            setBreadcrumbId(id);
            setIsEmptyVod(false);
        }

        // Track del evento de categoría únicamente para semanadelbienestarintegral
        if (id === TARGET_CATEGORY_ID && typeof window !== 'undefined' && typeof window.fbq === 'function') {
            // Si quieres "las categorías" como evento: aquí mando un custom event.
                // (Si prefieres otro nombre, dímelo y lo ajusto)
            window.fbq('trackCustom', 'CategoryView', {
                category_id: id,
                category_name: response.category_name
            });
        }
    }, [response, id]);

    const Grid = ({ specialRegister = false, childCategories = null }) => {
        if (childCategories) {
            return (
              <section className="gen-section-padding-3">
                  <div className="container">
                      <div className="row">
                          <div className="col-lg-12">
                              <div className="row">
                                  {childCategories.map((cat) => (
                                    <CategoryGridItems
                                      key={cat.category_id}
                                      category_id={cat.category_id}
                                      specialRegister={specialRegister}
                                    />
                                  ))}
                              </div>
                          </div>
                      </div>
                  </div>
              </section>
            );
        }

        vodList.sort(categoriesOrder);
        const gridItems = vodList.map((vod) => (
          <GridItem key={vod.stream_id} {...vod} specialRegister={specialRegister} />
        ));

        return (
          <section className="gen-section-padding-3">
              <div className="container">
                  <div className="row">
                      <div className="col-lg-12">
                          <div className="row">
                              {gridItems}
                          </div>
                      </div>
                  </div>
              </div>
          </section>
        )
    }

    if (id === SPECIAL_CATEGORY_ID) {
        const childCategories = (childCategoriesResponse?.category || []).filter(
          (cat) => cat.category_id !== BANNER_CATEGORY_ID
        );
        childCategories.sort(categoriesOrder);

        return (
          <div>
              <BreadcrumbWrapper id={id}>
                  <div className="d-flex justify-content-center align-items-center mt-4 pb-4">
                      <Link to="/sign-up?specialRegister=true" className="gen-button" style={{display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', backgroundColor: '#cb6015', borderRadius: '25px', padding: '12px 40px', marginRight: '35px'}}>Suscríbete</Link>
                      <Link to="/login?specialRegister=true" className="gen-button" style={{display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', backgroundColor: '#cb6015', borderRadius: '25px', padding: '12px 40px'}}>Inicia Sesión</Link>
                  </div>
              </BreadcrumbWrapper>
              <SliderFull category_id={BANNER_CATEGORY_ID} specialRegister={true} />
              <div className="pt-5">
                  {childCategoriesLoading ? (
                    <LoaderSpinner />
                  ) : (
                    <Grid specialRegister={true} childCategories={childCategories} />
                  )}
              </div>
          </div>
        );
    }

    if (!isEmptyVodList) {
        return (
          <div>
              <BreadcrumbWrapper id={breadcrumbId} />
              <Grid />
          </div>
        )
    }

    return <LoaderSpinner />;
};

export default CategoryPage;